import type { TransferFrame } from "./frames";

export type PlaybackStatus = "ready" | "streaming" | "paused";

export interface PlaybackSnapshot {
  status: PlaybackStatus;
  currentIndex: number;
  currentFrame: TransferFrame;
  frameDurationMs: number;
  loopCount: number;
  allFramesDisplayed: boolean;
  displayedFrameCount: number;
}

export class TransferFramePlayback {
  readonly #frames: readonly TransferFrame[];
  readonly #displayedFrameIds = new Set<string>();
  #currentIndex = 0;
  #frameDurationMs: number;
  #loopCount = 0;
  #status: PlaybackStatus = "ready";

  public constructor(frames: readonly TransferFrame[], frameDurationMs = 550) {
    if (frames.length === 0) {
      throw new Error("Playback needs at least one frame.");
    }
    this.#frames = Object.freeze([...frames]);
    this.#frameDurationMs = this.#validateDuration(frameDurationMs);
  }

  public get frames(): readonly TransferFrame[] {
    return this.#frames;
  }

  public get snapshot(): PlaybackSnapshot {
    const currentFrame = this.#frames[this.#currentIndex];
    if (currentFrame === undefined) {
      throw new Error("Playback cursor is outside its immutable frame set.");
    }
    return {
      status: this.#status,
      currentIndex: this.#currentIndex,
      currentFrame,
      frameDurationMs: this.#frameDurationMs,
      loopCount: this.#loopCount,
      allFramesDisplayed: this.#loopCount > 0,
      displayedFrameCount: this.#displayedFrameIds.size,
    };
  }

  public start(): PlaybackSnapshot {
    this.#status = "streaming";
    return this.snapshot;
  }

  public pause(): PlaybackSnapshot {
    if (this.#status === "streaming") {
      this.#status = "paused";
    }
    return this.snapshot;
  }

  public resume(): PlaybackSnapshot {
    if (this.#status === "paused") {
      this.#status = "streaming";
    }
    return this.snapshot;
  }

  public setFrameDuration(frameDurationMs: number): PlaybackSnapshot {
    this.#frameDurationMs = this.#validateDuration(frameDurationMs);
    return this.snapshot;
  }

  public advance(): PlaybackSnapshot {
    if (this.#status !== "streaming") {
      return this.snapshot;
    }

    const current = this.#frames[this.#currentIndex];
    if (current !== undefined) {
      this.#displayedFrameIds.add(current.id);
    }

    if (this.#currentIndex === this.#frames.length - 1) {
      this.#loopCount += 1;
      this.#currentIndex = 0;
    } else {
      this.#currentIndex += 1;
    }
    return this.snapshot;
  }

  public restartLoop(): PlaybackSnapshot {
    this.#currentIndex = 0;
    return this.snapshot;
  }

  #validateDuration(value: number): number {
    if (!Number.isFinite(value) || value < 150 || value > 3_000) {
      throw new Error("Frame duration must be between 150 and 3000 ms.");
    }
    return Math.round(value);
  }
}
